"use client";

import { type ReactNode, useState, createContext } from "react";
import { useTranslations } from "next-intl";
import {
	Menu,
	Heart,
	Sparkles,
	Search,
	Truck,
	HelpCircle,
	MessageSquare,
	ArrowRight,
	BookOpen,
	Layers,
	Calculator,
	Activity,
	GitCompareArrows,
} from "lucide-react";
import { getCategoryIcon } from "./mega-menu-panels";
import { Logo } from "../../logo";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
	SheetCloseButton,
} from "@/ui/components/ui/sheet";
import {
	Accordion,
	AccordionItemWithContext,
	AccordionTrigger,
	AccordionContent,
} from "@/ui/components/ui/accordion";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { cn } from "@/lib/utils";
import type { CategoryItem } from "./mega-menu-panels";

// Context to allow children to close the menu
const MobileMenuContext = createContext<{ close: () => void } | null>(null);

interface MobileMenuLabels {
	peptides: string;
	research: string;
	support: string;
	favorites: string;
	browseAll: string;
	allPeptides: string;
	quiz: string;
	guide: string;
	guides: string;
	partners: string;
	newsletter: string;
	shipping: string;
	faq: string;
	contact: string;
	stackBuilder: string;
	calculator: string;
	halfLifeCalculator: string;
	compare?: string;
	compareDesc?: string;
}

type Props = {
	children: ReactNode;
	/** Server-rendered account entry point (login/orders) — the only one below `lg`. */
	accountSlot?: ReactNode;
	/** Server-rendered campaign link; the server decides per request whether it exists. */
	campaignSlot?: ReactNode;
	categories?: CategoryItem[];
	labels?: MobileMenuLabels;
};

function MobileMenuLink({
	href,
	icon: Icon,
	children,
	onClick,
}: {
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	children: ReactNode;
	onClick?: () => void;
}) {
	return (
		<LinkWithChannel
			href={href}
			className="hover:bg-muted/70 flex items-center gap-3 rounded-xl px-2.5 py-2.5 text-[13px] font-medium text-foreground transition-all"
			onClick={onClick}
		>
			<div className="bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
				<Icon className="h-3.5 w-3.5 text-primary" />
			</div>
			{children}
		</LinkWithChannel>
	);
}

export const MobileMenu = ({ children, accountSlot, campaignSlot, categories, labels }: Props) => {
	const t = useTranslations("nav");
	const [isOpen, setIsOpen] = useState(false);

	const close = () => setIsOpen(false);

	const hasAccordionNav = categories && labels;

	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetTrigger asChild>
				<button
					type="button"
					className="text-foreground/80 hover:bg-foreground/[0.06] flex h-[38px] w-[38px] items-center justify-center rounded-full transition-colors hover:text-foreground lg:hidden"
					aria-label={t("openMenu")}
				>
					<Menu className="h-5 w-5" />
				</button>
			</SheetTrigger>
			<SheetContent
				side="top"
				className={cn(
					"flex max-h-[calc(100vh-24px)] w-auto flex-col gap-0 overflow-y-auto p-3.5",
					"border-foreground/[0.08] inset-x-3 top-3 rounded-[28px] border",
					"bg-card",
					"shadow-[0_24px_60px_rgba(14,23,38,0.18)]",
				)}
			>
				<SheetTitle className="sr-only">{t("menuTitle")}</SheetTitle>
				<SheetHeader className="flex items-center justify-between gap-4 border-0 px-1.5 pb-3.5 pt-1">
					<Logo />
					<div className="flex items-center gap-3">
						<SheetCloseButton className="static" closeLabel={t("closeMenu")} />
					</div>
				</SheetHeader>
				<nav className="flex-1 overflow-y-auto">
					<MobileMenuContext.Provider value={{ close }}>
						{hasAccordionNav ? (
							<div
								className="p-4"
								onClick={(e) => {
									if ((e.target as HTMLElement).closest("a")) {
										close();
									}
								}}
							>
								{/* Search bar slot */}
								{children}

								{/* Account slot — login/orders entry, server-rendered */}
								{accountSlot}

								{/* Campaign slot: server-rendered, present only while the campaign phase shows it */}
								{campaignSlot}

								<Accordion type="single" className="mt-4">
									{/* Peptides */}
									<AccordionItemWithContext value="peptides">
										<AccordionTrigger className="text-base font-semibold">{labels.peptides}</AccordionTrigger>
										<AccordionContent>
											<div className="space-y-1">
												{categories.map((cat) => (
													<MobileMenuLink
														key={cat.id}
														href={`/categories/${cat.slug}`}
														icon={getCategoryIcon(cat.slug)}
													>
														{cat.name}
													</MobileMenuLink>
												))}
												<MobileMenuLink href="/quiz" icon={Sparkles}>
													{labels.quiz}
												</MobileMenuLink>
												<MobileMenuLink href="/stack-builder" icon={Layers}>
													{labels.stackBuilder}
												</MobileMenuLink>
												<MobileMenuLink href="/peptide-calculator" icon={Calculator}>
													{labels.calculator}
												</MobileMenuLink>
												<MobileMenuLink href="/half-life-calculator" icon={Activity}>
													{labels.halfLifeCalculator}
												</MobileMenuLink>
												{labels.compare && (
													<MobileMenuLink href="/peptides/compare" icon={GitCompareArrows}>
														{labels.compare}
													</MobileMenuLink>
												)}
												<MobileMenuLink href="/guides" icon={BookOpen}>
													{labels.guides}
												</MobileMenuLink>
												<LinkWithChannel
													href="/products"
													className="mt-2 flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-primary"
												>
													{labels.browseAll}
													<ArrowRight className="h-3.5 w-3.5" />
												</LinkWithChannel>
											</div>
										</AccordionContent>
									</AccordionItemWithContext>

									{/* Research */}
									<AccordionItemWithContext value="research">
										<AccordionTrigger className="text-base font-semibold">{labels.research}</AccordionTrigger>
										<AccordionContent>
											<div className="space-y-1">
												<MobileMenuLink href="/peptides" icon={Search}>
													{labels.guide}
												</MobileMenuLink>
												<MobileMenuLink href="/peptide-calculator" icon={Calculator}>
													{labels.calculator}
												</MobileMenuLink>
												<MobileMenuLink href="/half-life-calculator" icon={Activity}>
													{labels.halfLifeCalculator}
												</MobileMenuLink>
												{labels.compare && (
													<MobileMenuLink href="/peptides/compare" icon={GitCompareArrows}>
														{labels.compare}
													</MobileMenuLink>
												)}
												<MobileMenuLink href="/guides" icon={BookOpen}>
													{labels.guides}
												</MobileMenuLink>
											</div>
										</AccordionContent>
									</AccordionItemWithContext>

									{/* Support */}
									<AccordionItemWithContext value="support">
										<AccordionTrigger className="text-base font-semibold">{labels.support}</AccordionTrigger>
										<AccordionContent>
											<div className="space-y-1">
												<MobileMenuLink href="/shipping" icon={Truck}>
													{labels.shipping}
												</MobileMenuLink>
												<MobileMenuLink href="/faq" icon={HelpCircle}>
													{labels.faq}
												</MobileMenuLink>
												<MobileMenuLink href="/contact" icon={MessageSquare}>
													{labels.contact}
												</MobileMenuLink>
											</div>
										</AccordionContent>
									</AccordionItemWithContext>
								</Accordion>

								{/* Favorites direct link */}
								<div className="mt-4 border-t border-border pt-4">
									<LinkWithChannel
										href="/favorites"
										className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
									>
										<div className="bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md">
											<Heart className="h-3.5 w-3.5 text-primary" />
										</div>
										{labels.favorites}
									</LinkWithChannel>
								</div>
							</div>
						) : (
							<ul
								className="flex flex-col whitespace-nowrap p-4 [&>*:nth-child(n+2)]:border-t [&>*:nth-child(n+2)]:border-border [&>li]:py-4"
								id="mobile-menu"
								onClick={(e) => {
									if ((e.target as HTMLElement).closest("a")) {
										close();
									}
								}}
							>
								{children}
							</ul>
						)}
					</MobileMenuContext.Provider>
				</nav>
			</SheetContent>
		</Sheet>
	);
};
